import { useState, type ReactElement } from "react";
import { Separator } from "@heroui/react";
import "../app/App.styles.scss";
import { Link, useNavigate } from "react-router";
import { HomeHeroSearch } from "@/features/home/HomeHeroSearch";
import { HomeRecipeSampling } from "@/features/home/HomeRecipeSampling";
import { HomeChapters } from "@/features/home/HomeChapters";
import { HomeShelves } from "@/features/home/HomeShelves";
import { HomeColophon } from "@/features/home/HomeColophon";
import { SectionDivider } from "@/components/SectionDivider";
import { useFavoriteRecipesSample } from "@/hooks/useFavoriteRecipesSample";
import { useUntriedRecipes } from "@/hooks/useUntriedRecipes";
import { useRecentlyAddedRecipes } from "@/hooks/useRecentlyAddedRecipes";

const FavoritesEmpty = () => (
    <div className="flex flex-col items-center text-center">
        <img
            src="/savory-grimoire-sm.png"
            alt=""
            aria-hidden="true"
            className="h-16 sm:h-20 w-auto opacity-40 mb-4 select-none"
        />
        <h3 className="font-serif text-lg sm:text-xl text-[var(--color-secondary-500)] mb-2">
            No favorites yet
        </h3>
        <p className="font-serif italic text-base sm:text-lg text-[var(--fg-muted)] max-w-md">
            Mark a favorite once a recipe is worthy to be revisited.
        </p>
    </div>
);

const UntriedEmpty = () => (
    <div className="flex flex-col items-center text-center">
        <img
            src="/savory-grimoire-sm.png"
            alt=""
            aria-hidden="true"
            className="h-16 sm:h-20 w-auto opacity-40 mb-4 select-none"
        />
        <h3 className="font-serif text-lg sm:text-xl text-[var(--color-secondary-500)] mb-2">
            You've tasted every realm
        </h3>
        <p className="font-serif italic text-base sm:text-lg text-[var(--fg-muted)] max-w-md">
            A rare feat — there's nothing left untried in your grimoire.
        </p>
    </div>
);

export const Home = (): ReactElement => {
    const navigate = useNavigate();
    const [homeQuery, setHomeQuery] = useState("");
    const handleSearch = (query: string) => {
        const params = new URLSearchParams();
        params.append("query", query);
        navigate(`/recipes?${params.toString()}`);
    };

    const favoritesSample = useFavoriteRecipesSample(3);
    const untried = useUntriedRecipes(3);
    const recentlyAdded = useRecentlyAddedRecipes(3);

    return (
        <>
            <section
                className="w-full h-[calc(100vh-64px)] flex flex-col items-center justify-center bg-cover bg-center relative"
                style={{ backgroundImage: "url('/default-home.jpg')" }}
            >
                <div className="absolute inset-0 backdrop-filter backdrop-brightness-110 backdrop-contrast-110 backdrop-saturate-120 bg-[rgba(26,26,26,0.6)]"></div>

                <div className="relative z-20 flex flex-col items-center w-full px-6">
                    <div className="flex flex-col items-center text-center max-w-3xl">
                        <h1 className="text-4xl leading-12 sm:text-5xl sm:leading-14 mb-2 text-white">
                            <span>A RECIPE BOOK</span>
                            <br />
                            <span>OF TASTES AND TALES...</span>
                        </h1>
                        <Separator className="bg-white w-22 my-4" />
                        <p className="text-white text-2xl sm:text-3xl">
                            Every kitchen spell you've crafted and kept—your entire culinary grimoire at a glance.
                        </p>
                    </div>

                    <div className="mt-12 sm:mt-16 flex flex-col items-center gap-6 w-full">
                        <HomeHeroSearch
                            value={homeQuery}
                            onChange={setHomeQuery}
                            onSubmit={handleSearch}
                            onClear={() => setHomeQuery("")}
                        />
                        <Link
                            to="/recipes"
                            className="inline-flex items-center justify-center min-w-[240px] h-[52px] px-6 rounded-lg bg-primary-500 hover:bg-primary-600 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 font-display text-base tracking-[0.15em] uppercase"
                        >
                            View Recipes
                        </Link>
                    </div>
                </div>
            </section>

            <SectionDivider />

            <HomeChapters />

            <HomeRecipeSampling
                title="Discover New Realms of Taste"
                subhead="Pages you've left unturned."
                seeAllHref="/recipes?hasMadeIt=false"
                seeAllLabel="See all not yet made"
                surface="warm"
                recipes={untried.data ?? []}
                isLoading={untried.isLoading}
                isError={untried.isError}
                emptyState={<UntriedEmpty />}
            />

            <HomeShelves />

            <HomeRecipeSampling
                title="Tales Worth Retelling"
                subhead="The pages you keep returning to."
                seeAllHref="/recipes?isFavorite=true"
                seeAllLabel="See all your favorites"
                surface="warm"
                recipes={favoritesSample.data}
                isLoading={favoritesSample.isLoading}
                isError={favoritesSample.isError}
                emptyState={<FavoritesEmpty />}
            />

            <HomeRecipeSampling
                title="Recently Scribed"
                subhead="New spells in the grimoire."
                seeAllHref="/recipes"
                seeAllLabel="See all recipes"
                surface="cream"
                recipes={recentlyAdded.data ?? []}
                isLoading={recentlyAdded.isLoading}
                isError={recentlyAdded.isError}
                showAddedAgo
            />

            <HomeColophon />
        </>
    );
};
